import { useState } from "react";
import {
  PanelShell, Badge, StatChip, Button, ErrorState, ApiResourceView, TextField, CheckboxRow, tokens,
  DJSEMApiClient, useApiResource, useApiAction,
  VIDEO_OUTPUT_TYPE_OPTIONS, describeVideoOutputType, checkVideoSourceEligibility, canCreateVideoJob,
  VIDEO_RIGHTS_CONFIRMATION_COPY, videoJobStatusTone, videoJobStatusLabel, type DJSEMAIVideoOutputTypeUI, type VideoJobStatusUI,
  videoOutputReviewTone, videoOutputReviewLabel, videoOutputActionsFor, type VideoOutputReviewStateUI,
} from "../index";
import type { DJSEMPlatform } from "../platform";
import { toBadgeTone } from "./shared";

interface AIVideoJobUI { id: string; status: VideoJobStatusUI; providerId?: string; progress?: number; warnings?: string[]; errors?: string[]; }

type LocalRenderState =
  | { status: "idle" }
  | { status: "rendering" }
  | { status: "done"; outputPath: string }
  | { status: "error"; error: string };

/**
 * AI Video Generation Studio (Prompt 22B). Renders via the real local
 * TemplateAudioReactiveVideoProvider through `bin/djsem video render` (apps/ai-video-renderer)
 * — Cloudflare Workers can't run ffmpeg/native canvas, so a job created here stays queued for
 * that local run rather than completing inline (same honest gap as Stem Studio's real-model path).
 *
 * Real local rendering needs native ffmpeg/canvas — a browser can't do it. Same shape as
 * LiveModule's desktop-only gate: tell web users plainly rather than show a form that can never
 * actually produce a video. A plain wrapper (no hooks) so the platform branch can early-return
 * without ever violating rules-of-hooks in the real form below.
 *
 * @param client - The API client passed to the video generation form.
 * @param platform - The current platform; determines whether the real form or the
 *   desktop-only notice is shown.
 * @returns A desktop-only notice when local file access is unavailable, or the full AI Video
 *   Generation form.
 */
export function VideoGenerationModule({ client, platform }: { client: DJSEMApiClient; platform: DJSEMPlatform }) {
  if (!platform.files.available) {
    return (
      <PanelShell title="AI Video Generation — audio-reactive visualizer" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 }}>
          <div style={{ fontSize: tokens.text.lg, color: tokens.color.text }}>
            AI video rendering runs in the DJSEM desktop app.
          </div>
          <p style={{ margin: 0, color: tokens.color.textDim, fontSize: tokens.text.md, lineHeight: 1.55 }}>
            Generating a real audio-reactive video needs local ffmpeg, which only the desktop
            Studio has. The browser app covers your prep workflow — library, sets, brand, release
            assets. Everything you prepare here syncs to desktop automatically.
          </p>
          <div style={{ marginTop: 4 }}>
            <Badge tone="neutral">Desktop feature</Badge>
          </div>
        </div>
      </PanelShell>
    );
  }
  return <VideoGenerationForm client={client} platform={platform} />;
}

function VideoGenerationForm({ client, platform }: { client: DJSEMApiClient; platform: DJSEMPlatform }) {
  const [projectId, setProjectId] = useState("");
  const [pickedFile, setPickedFile] = useState<{ path: string; name: string; sizeBytes: number } | null>(null);
  const [outputType, setOutputType] = useState<DJSEMAIVideoOutputTypeUI>("full_track_visualizer");
  const [durationSeconds, setDurationSeconds] = useState(15);
  const [rightsChecked, setRightsChecked] = useState(false);
  const [creditChecked, setCreditChecked] = useState(false);
  const [lastJob, setLastJob] = useState<AIVideoJobUI | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [renderState, setRenderState] = useState<LocalRenderState>({ status: "idle" });

  const eligibility = checkVideoSourceEligibility({ hasLocalAudioFile: !!pickedFile, sourcePlatform: "local", rightsState: "unknown" });
  const canRun = eligibility.eligibility === "eligible" && canCreateVideoJob({ rightsChecked, creditChecked });
  const preset = describeVideoOutputType(outputType);
  const durationMs = Math.round(durationSeconds * 1000);

  const pickFile = async () => {
    const picked = await platform.files.pickAudioFile();
    if (picked) setPickedFile(picked);
  };

  const estimate = useApiAction((input: Parameters<DJSEMApiClient["estimateAIVideo"]>[0]) => client.estimateAIVideo(input));
  const runJob = useApiAction(async () => {
    const reqRes = await client.createAIVideoRequest({
      projectId, outputType, aspectRatio: preset?.aspectRatio ?? "16:9", durationMs,
      format: "mp4", qualityMode: "standard", variantCount: 1, sourceAudioRef: pickedFile?.path ?? "",
      userConfirmedRightsWarning: rightsChecked, userConfirmedCreditUsage: creditChecked,
    });
    if (!reqRes.ok) return reqRes;
    const jobRes = await client.createAIVideoJob(reqRes.data.requestId);
    if (jobRes.ok) { setLastJob(jobRes.data.job as AIVideoJobUI); setSelectedJobId((jobRes.data.job as AIVideoJobUI).id); }
    return jobRes;
  });

  const renderLocally = async () => {
    if (!pickedFile) return;
    setRenderState({ status: "rendering" });
    const result = await platform.files.renderVideo({
      audioPath: pickedFile.path, outputType, aspectRatio: preset?.aspectRatio ?? "16:9",
      durationMs, format: "mp4", qualityMode: "standard",
    });
    setRenderState(result.ok ? { status: "done", outputPath: result.outputPath } : { status: "error", error: result.error });
  };

  const jobsList = useApiResource(() => client.listAIVideoJobs(), [], (d) => (d.jobs?.length ?? 0) === 0);

  return (
    <>
      <PanelShell title="AI Video Generation — audio-reactive visualizer" style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: tokens.color.accent, marginBottom: 10, fontStyle: "italic" }}>
          Renders a real, deterministic audio-reactive visualizer locally — not a generative model.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 480 }}>
          <TextField placeholder="Project ID" value={projectId} onChange={setProjectId} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Button onClick={() => void pickFile()}>Choose audio file…</Button>
            {pickedFile ? (
              <span style={{ fontSize: 12, color: tokens.color.textDim }}>
                {pickedFile.name} · {(pickedFile.sizeBytes / (1024 * 1024)).toFixed(1)} MB
              </span>
            ) : <span style={{ fontSize: 12, color: tokens.color.muted }}>No file chosen</span>}
          </div>
          <label style={{ fontSize: 12, color: tokens.color.textDim, display: "flex", alignItems: "center", gap: 6 }}>
            Duration
            <input type="number" min={1} max={600} value={durationSeconds}
              onChange={(e: { target: { value: string } }) => setDurationSeconds(Number(e.target.value) || 1)}
              style={{ width: 64, padding: 6, fontSize: 12, background: tokens.color.bg, color: tokens.color.text, border: `1px solid ${tokens.color.border}`, borderRadius: 4 }} />
            seconds
          </label>
        </div>

        <div style={{ marginTop: 10 }}>
          {eligibility.eligibility === "eligible"
            ? <Badge tone="ok">eligible</Badge>
            : <Badge tone="warn">{eligibility.eligibility.replace(/_/g, " ")}</Badge>}
          <div style={{ fontSize: 11, color: tokens.color.textDim, marginTop: 4 }}>{eligibility.reason}</div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: tokens.color.text, marginBottom: 6 }}>Output type</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {VIDEO_OUTPUT_TYPE_OPTIONS.map((opt) => (
              <button key={opt.outputType} onClick={() => setOutputType(opt.outputType)} title={opt.bestFor} style={{
                fontSize: 11, padding: "5px 10px", borderRadius: 4, cursor: "pointer",
                background: outputType === opt.outputType ? tokens.color.accent : tokens.color.surface2,
                color: outputType === opt.outputType ? "#12100a" : tokens.color.text,
                border: `1px solid ${outputType === opt.outputType ? tokens.color.accentDim : tokens.color.border}`,
              }}>{opt.label}</button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 14, padding: 10, background: tokens.color.surface2, borderRadius: 4 }}>
          {VIDEO_RIGHTS_CONFIRMATION_COPY.map((line, i) => (
            <div key={i} style={{ fontSize: 11, color: tokens.color.textDim, marginBottom: 4 }}>{line}</div>
          ))}
          <CheckboxRow checked={rightsChecked} onChange={setRightsChecked}>I confirm I own or am licensed to use this audio and any referenced assets.</CheckboxRow>
          <CheckboxRow checked={creditChecked} onChange={setCreditChecked}>I understand this job consumes credits.</CheckboxRow>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <Button
            disabled={eligibility.eligibility !== "eligible" || !projectId || estimate.pending}
            onClick={() => void estimate.run({ projectId, outputType, aspectRatio: preset?.aspectRatio })}
          >{estimate.pending ? "Estimating…" : "Estimate"}</Button>
          <Button disabled={!canRun || !projectId || !pickedFile || runJob.pending} onClick={() => void runJob.run()}>
            {runJob.pending ? "Running…" : "Confirm & run"}
          </Button>
          <Button disabled={!canRun || !pickedFile || renderState.status === "rendering"} onClick={() => void renderLocally()}>
            {renderState.status === "rendering" ? "Rendering…" : "Render locally"}
          </Button>
        </div>

        {renderState.status === "done" ? (
          <div style={{ marginTop: 10 }}>
            <Badge tone="ok">Rendered</Badge>
            <span style={{ fontSize: 11, color: tokens.color.textDim, marginLeft: 8 }}>{renderState.outputPath}</span>
            <button onClick={() => platform.files.revealInFolder(renderState.outputPath)} style={{ fontSize: 11, color: tokens.color.accent, marginLeft: 8, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              Reveal in folder
            </button>
          </div>
        ) : null}
        {renderState.status === "error" ? <div style={{ marginTop: 10 }}><ErrorState detail={renderState.error} /></div> : null}

        {estimate.view?.state === "ready" && estimate.view.data ? (
          <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <StatChip label="Credits" value={String(estimate.view.data.estimatedCreditCost)} />
            {estimate.view.data.resolvedModelId ? <StatChip label="Model" value={estimate.view.data.resolvedModelId} tone="accent" /> : null}
            {estimate.view.data.modelResolutionBlockers.length > 0 ? <Badge tone="warn">{estimate.view.data.modelResolutionBlockers[0]}</Badge> : null}
          </div>
        ) : null}
        {estimate.view?.state === "error" ? <ErrorState detail={estimate.view.message} /> : null}

        {lastJob ? (
          <div style={{ marginTop: 12 }}>
            <Badge tone={toBadgeTone(videoJobStatusTone(lastJob.status))}>{videoJobStatusLabel(lastJob.status)}</Badge>
            <span style={{ fontSize: 11, color: tokens.color.textDim, marginLeft: 8 }}>job {lastJob.id}</span>
            {lastJob.status === "completed" ? (
              <button onClick={() => setSelectedJobId(lastJob.id)} style={{ fontSize: 11, color: tokens.color.accent, marginLeft: 8, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                Open review to approve video
              </button>
            ) : null}
          </div>
        ) : null}
      </PanelShell>

      <PanelShell title="Recent video jobs" style={{ marginTop: 12 }}>
        <ApiResourceView
          resource={jobsList}
          empty={{ title: "No video jobs yet", detail: "Run your first generation above." }}
          render={(data) => (
            <>
              {((data.jobs ?? []) as unknown as AIVideoJobUI[]).map((j) => (
                <div key={j.id} onClick={() => setSelectedJobId(j.id)} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", cursor: "pointer" }}>
                  <span>{j.id}{j.providerId ? <span style={{ fontSize: 10, opacity: 0.6 }}> · {j.providerId}</span> : null}</span>
                  <Badge tone={toBadgeTone(videoJobStatusTone(j.status))}>{videoJobStatusLabel(j.status)}</Badge>
                </div>
              ))}
            </>
          )}
        />
      </PanelShell>

      {selectedJobId ? <VideoReviewPanel client={client} jobId={selectedJobId} /> : null}
    </>
  );
}

interface AIVideoOutputUI {
  id: string; fileRef: string; width: number; height: number; durationMs: number; format: string;
  providerId: string; warnings: string[]; reviewStatus: string;
}

/** AI video output review — approve/reject/needs-review only; never shows "completed" until
 * real output asset refs exist (enforced server-side by AIVideoGenerationService.completeJob). */
function VideoReviewPanel({ client, jobId }: { client: DJSEMApiClient; jobId: string }) {
  const job = useApiResource(() => client.getAIVideoJob(jobId), [jobId]);
  const approve = useApiAction((outputId: string, state: string) => client.setAIVideoOutputApproval(outputId, state));

  return (
    <ApiResourceView
      resource={job}
      empty={{ title: "No video outputs yet", detail: "This job hasn't produced outputs — check its status above." }}
      render={(data) => {
        const outputs = ((data.outputs ?? []) as unknown as AIVideoOutputUI[]);
        if (outputs.length === 0) {
          return <PanelShell title="Video review" style={{ marginTop: 12 }}><Badge tone="neutral">No video outputs yet</Badge></PanelShell>;
        }
        return (
          <PanelShell title={`Video review — ${jobId}`} style={{ marginTop: 12 }}>
            {outputs.map((o) => {
              const reviewState = (o.reviewStatus as VideoOutputReviewStateUI) ?? "not_reviewed";
              const actions = videoOutputActionsFor(reviewState);
              return (
                <div key={o.id} style={{ padding: "8px 0", borderBottom: `1px solid ${tokens.color.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13 }}>{o.width}×{o.height} · {o.format.toUpperCase()}{o.durationMs ? ` · ${(o.durationMs / 1000).toFixed(1)}s` : ""}</span>
                    <Badge tone={toBadgeTone(videoOutputReviewTone(reviewState))}>{videoOutputReviewLabel(reviewState)}</Badge>
                  </div>
                  {o.warnings.length > 0 ? (
                    <div style={{ fontSize: 11, color: "#e8b24a", marginTop: 4 }}>{o.warnings.join(" · ")}</div>
                  ) : null}
                  <div style={{ fontSize: 11, color: tokens.color.textDim, marginTop: 4 }}>{o.fileRef} · provider {o.providerId}</div>
                  {actions.length > 0 ? (
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      {actions.map((a) => (
                        <button key={a} disabled={approve.pending} onClick={() => void approve.run(o.id, a === "approve" ? "approved" : a === "reject" ? "rejected" : "needs_review").then(job.refetch)}
                          style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer", background: "transparent", color: tokens.color.accent, border: `1px solid ${tokens.color.accentDim}` }}>
                          {a}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </PanelShell>
        );
      }}
    />
  );
}
